import React, { useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EditIcon from '@material-ui/icons/Edit';
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import { DialogModal, useHandleOpen } from '@engloba-tech/englobity';
import { NAME_SPACE } from 'i18n';
import { ViewTable, execConfirmModal, ButtonAdd, ButtonDelete, Button } from 'shared';
import { ROLES, UserContext } from 'auth';
import { PATH } from 'app.routes.const';
import { useAnalyticsReportsPaginated } from './useAnalyticsReportsPaginated';
import { useAnalyticsReportsBuildTable } from './useAnalyticsReportsBuildTable';
import { useListAnalyticsReportsStyles } from './listAnalyticsReports.styles';
import { FormSendMail } from './FormSendMail';

export function ListAnalyticsReports() {
  const classes = useListAnalyticsReportsStyles();
  const {
    reports,
    getReports,
    handleDownload,
    deleteReports,
    sendByMail,
    errorInfo,
    actionErrorInfo,
    setActionErrorInfo,
    isSending
  } = useAnalyticsReportsPaginated();
  const { t } = useTranslation(NAME_SPACE.ANALYTICS_REPORTS);
  const history = useHistory();
  const { user } = useContext(UserContext);
  const $formSendMail = useRef(null);
  const isAdmin = user?.role === ROLES.ADMIN;
  const isAnalytics = user?.role === ROLES.ANALYTICS;

  const {
    isOpen: isOpenSendMailModal,
    handleClose: handleCloseSendMailModal,
    handleOpen: handleOpenSendMailModal
  } = useHandleOpen(false);

  const { rows, headCells } = useAnalyticsReportsBuildTable(reports, t);

  function handleSendMail() {
    setActionErrorInfo();
    handleOpenSendMailModal();
  }

  function composeActionButtons() {
    if (!isAdmin && !isAnalytics) {
      return [];
    }

    let actionButtons = isAdmin
      ? [
          {
            condition: isAnySelected => !isAnySelected,
            cb: handleAdd,
            component: ButtonAdd
          },
          {
            condition: isAnySelected => isAnySelected,
            resetOnCB: true,
            cb: handleDelete,
            component: ButtonDelete
          }
        ]
      : [];

    actionButtons = [
      ...actionButtons,
      {
        condition: isAnySelected => !isAnySelected,
        title: t('actions.sendByMail'),
        name: 'send',
        properties: [],
        label: 'send',
        cb: handleSendMail,
        icon: <MailOutlineIcon />,
        component: Button
      }
    ];

    return actionButtons;
  }

  function handleEdit([reportId]) {
    history.push(PATH.ANALYTICS_REPORTS.EDIT.replace(':id', reportId));
  }

  function handleAdd() {
    history.push(PATH.ANALYTICS_REPORTS.CREATE);
  }

  async function handleDelete(reportsToDelete) {
    try {
      await execConfirmModal({
        title: t('common:actions.remove'),
        description:
          reportsToDelete.length > 1
            ? t('removeConfirm.message', { length: reportsToDelete.length })
            : t('removeConfirm.singularMessage', {
                name: reportsToDelete[0].name
              }),
        confirmText: t('common:actions.accept'),
        declineText: t('common:actions.cancel'),
        confirmClassName: classes.buttonAdd,
        declineClassName: classes.buttonDelete
      });
      await deleteReports(reportsToDelete);
    } catch (err) {}
  }

  function handleCloseSendMail() {
    handleCloseSendMailModal();
    setActionErrorInfo();
  }

  function handleModalAcceptSendMail() {
    $formSendMail.current.submit();
  }

  async function handleSendMailSubmit(request) {
    const success = await sendByMail(request);
    if (success) {
      handleCloseSendMail();
    }
  }

  return (
    <div className={classes.reports}>
      <ViewTable
        elevation
        spaced
        errors={errorInfo}
        rows={rows}
        cells={headCells}
        defaultOrderBy={{ property: 'year', direction: 'desc' }}
        mainKey={'name'}
        onFetchData={getReports}
        totalRows={reports.total}
        serverSidePaging
        allowRowSelection={isAdmin}
        emptyText={t('empty')}
        actions={[
          {
            title: isAdmin ? t('actions.edit') : t('actions.view'),
            name: 'edit',
            properties: ['id'],
            label: 'edit this row',
            cb: handleEdit,
            icon: isAdmin ? <EditIcon /> : <VisibilityIcon />
          },
          {
            condition: element => !element['reportFileUrl'],
            whenConditionTrue: null,
            title: t('actions.download'),
            name: 'download',
            properties: ['reportFileUrl', 'reportFileName'],
            label: 'download',
            cb: handleDownload,
            icon: <CloudDownloadOutlinedIcon />
          }
        ]}
        toolbar={{
          title: t('name'),
          actions: composeActionButtons()
        }}
      />
      <DialogModal
        maxWidth="md"
        className={classes.modal}
        title={t('sendByMail.dialogTitle')}
        description=""
        isOpen={isOpenSendMailModal}
        onClose={handleCloseSendMail}
        buttons={[
          {
            children: <CloseIcon />,
            text: t('common:actions.cancel'),
            type: 'secondary',
            onClick: handleCloseSendMail,
            className: classes.buttonDelete
          },
          {
            children: <DoneIcon />,
            text: t('common:actions.accept'),
            type: 'primary',
            onClick: handleModalAcceptSendMail,
            className: classes.buttonAdd,
            disabled: isSending
          }
        ]}
      >
        <FormSendMail formRef={$formSendMail} onSubmit={handleSendMailSubmit} errors={actionErrorInfo} />
      </DialogModal>
    </div>
  );
}
